import React, { useMemo } from 'react';
import { useEmoji } from '@jira/polaris__lib-emoji-picker/src/useEmoji.tsx';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import Emoji from '@atlaskit/emoji/emoji';
import type { Size, IconProps, NewCoreIconProps } from '@atlaskit/icon/types';
import { sizes } from '@atlaskit/icon/constants/default';
import IconTile from '@atlaskit/icon/icon-tile';
import { token } from '@atlaskit/tokens';
import PlanIcon from '@atlaskit/icon-lab/core/plan';
import { fg } from '@atlassian/jira-feature-gating';

type CollectionEmojiProps = IconProps & {
	fitToHeight?: number;
};

type CollectionEmojiPropsNew = NewCoreIconProps & {
	fitToHeight?: number;
};

const EmojiIcon = ({
	emojiDesc,
	...props
}: CollectionEmojiPropsNew & { emojiDesc: NonNullable<ReturnType<typeof useEmoji>> }) => {
	let fitToHeight = 24;
	if (props.fitToHeight) {
		fitToHeight = props.fitToHeight;
	} else if (props.size && typeof props.size === 'string') {
		fitToHeight = parseInt(sizes[props.size], 10) || 24;
	}
	return <Emoji emoji={emojiDesc} showTooltip fitToHeight={fitToHeight} />;
};

const DefaultRoadmapTile = (props: CollectionEmojiPropsNew) => {
	return <IconTile icon={PlanIcon} label={props.label} appearance="gray" size="small" />;
};

const useCollectionEmojiIconNew = (
	emoji?: string,
): ((props: CollectionEmojiPropsNew) => React.JSX.Element) => {
	const emojiDesc = useEmoji(emoji);

	return useMemo(() => {
		if (emojiDesc) {
			return (props: CollectionEmojiPropsNew) => <EmojiIcon emojiDesc={emojiDesc} {...props} />;
		}

		return PlanIcon;
	}, [emojiDesc]);
};

export const useCollectionEmojiTile = (
	emoji?: string,
): ((props: CollectionEmojiPropsNew) => React.JSX.Element) => {
	const emojiDesc = useEmoji(emoji);

	return useMemo(() => {
		if (emojiDesc) {
			return (props: CollectionEmojiPropsNew) => <EmojiIcon emojiDesc={emojiDesc} {...props} />;
		}

		return DefaultRoadmapTile;
	}, [emojiDesc]);
};

export const useCollectionEmojiIconOld = (
	emoji?: string,
): ((props: CollectionEmojiProps) => React.JSX.Element) => {
	const emojiDesc = useEmoji(emoji);

	return useMemo(() => {
		if (emojiDesc) {
			return (props: CollectionEmojiProps) => {
				let fitToHeight = 24;
				if (props.fitToHeight) {
					fitToHeight = props.fitToHeight;
				} else if (props.size) {
					fitToHeight = parseInt(sizes[props.size], 10);
				}

				return <Emoji emoji={emojiDesc} showTooltip fitToHeight={fitToHeight} />;
			};
		}

		return (props: IconProps) => {
			const tileSize = sizeMapping(props.size || 'medium');
			if (tileSize === null) {
				// For '16' size, render the icon directly without a tile
				return <PlanIcon label={props.label} color={token('color.icon.accent.gray')} />;
			}
			return <IconTile icon={PlanIcon} label={props.label} appearance="gray" size={tileSize} />;
		};
	}, [emojiDesc]);
};

export const useCollectionEmojiIcon: typeof useCollectionEmojiIconNew = functionWithCondition(
	() => fg('jpd-enable-roadmaps-recents-fe'),
	useCollectionEmojiIconNew,
	// oxlint-disable-next-line typescript/consistent-type-assertions
	useCollectionEmojiIconOld as typeof useCollectionEmojiIconNew,
);

// remove with jpd-enable-roadmaps-recents-fe
const sizeMapping = (size: Size): 'xsmall' | 'small' | 'medium' | 'large' | null => {
	switch (size) {
		case 'small':
			return null;
		case 'medium':
			return 'small';
		case 'large':
			return 'medium';
		case 'xlarge':
			return 'large';
		default:
			return 'small';
	}
};
